import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import _ from 'lodash';
import {
  Add,
  ArrowDropDown,
  ArrowDropUp,
  Delete,
  Edit,
} from '@material-ui/icons';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
} from '@material-ui/core';

import RenderProfilePic from '../../extras/RenderProfilePic';
import awsConfig from '../../aws-exports';
import {
  addNewTheme,
  deleteTheme,
  getAllQuesModules,
  updateTheme,
  getTracks,
} from '../../services/apiService';
import { setQuestionLevels } from '../../redux/reducers/questionLevelsReducer';
import { COURSE_TYPE, COURSE_TYPE_NAMES_MAPPING } from '../../constants';
import CustomSwitch from '../../extras/CustomSwitch';
import RichTextEditor from '../../extras/RichTextEditor';

class AddOrEditSession extends Component {
  constructor() {
    super();

    this.state = {
      theme: {},
      iconInputRef: React.createRef(),
      iconLocalFile: null,
      expandedSubTopic: {},
      isLoading: {},
      deleteConfirmPopUp: null,
      tracks: [],
    };
  }

  componentDidMount = async () => {
    if (this.props.location.state?.isEditMode) {
      const theme = this.props.location.state?.theme || {};
      this.setState({ theme });
    }
    try {
      if (!this.props.modules.length)
        this.props.dispatch(setQuestionLevels(await getAllQuesModules()));
    } catch (err) {
      console.error(err);
    }

    try {
      const response = await getTracks();
      const tracks = Array.isArray(response) 
        ? response 
        : response?.data || response?.tracks || [];
      this.setState({ tracks });
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
      this.setState({ tracks: [] });
    }
  };

  uploadIconToS3 = async () => {
    try {
      const file = this.state.iconLocalFile;
      const { key } = await Storage.put(
        `themeIcon/${uuidv4()}.${file.type.split('/')[1]}`,
        file,
        { contentType: file.type }
      );
      return `https://${awsConfig.Storage.AWSS3.bucket}.s3.${awsConfig.Storage.AWSS3.region}.amazonaws.com/public/${key}`;
    } catch (err) {
      console.error(err);
      toast.error('Uploading Theme Icon Failed!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
    }
  };

  addNewThemeHandler = async (themeData) => {
    try {
      await addNewTheme(themeData);
      toast.dark('New Theme Added Successfully', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      this.props.history.goBack();
    } catch (err) {
      console.error(err);
      toast.error('Add New Theme Failed, Please try again later!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
    }
  };

  updateThemeHandler = async (themeData) => {
    try {
      await updateTheme(themeData);
      toast.dark('Theme Updated Successfully', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
    } catch (err) {
      console.error(err);
      toast.error('Updating Theme Failed, Please try again later!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
    }
  };

  deleteThemeHandler = async (themeId) => {
    try {
      this.setState({ isDeleting: { deleting: true } });
      await deleteTheme(themeId);
      toast.dark('Theme Deleted Successfully', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      this.props.history.goBack();
    } catch (err) {
      console.error(err);
      toast.error('Deleting Theme Failed, Please try again later!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      this.setState({ isDeleting: {}, deleteConfirmPopUp: null });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ isLoading: { saving: true } });
    const theme = this.state.theme;
    if (this.state.iconLocalFile) {
      const iconUrl = await this.uploadIconToS3();
      theme.icon = iconUrl;
    }
    if (this.props.location.state?.isEditMode) {
      await this.updateThemeHandler(theme);
    } else {
      await this.addNewThemeHandler(theme);
    }
    this.props.history.push('/session-builder');
  };

  render = () => (
    <div>
      <header className='flex items-center gap-8'>
        <h1 className='text-3xl font-semibold'>
          {this.props.location.state?.isEditMode ? 'Edit' : 'Add New'} Theme
        </h1>

        {this.props.location.state?.isEditMode && (
          <span className='flex-grow flex justify-between'>
            <Link
              to='/new-session'
              className='bg-white text-blue-c1 font-medium border border-blue-c1 rounded-md px-6 py-2'
            >
              Add New Theme
            </Link>

            <button
              onClick={() =>
                this.setState({
                  deleteConfirmPopUp: {
                    type: 'Theme',
                    name: this.state.theme.name,
                    delete: () => this.deleteThemeHandler(this.state.theme.id),
                  },
                })
              }
              disabled={this.state.isDeleting}
              className='bg-white rounded-md px-4 py-2 flex items-center gap-2 text-gray-500 hover:text-red-c1'
            >
              <Delete />
              <span className='font-medium'>Delete Theme</span>
            </button>
          </span>
        )}
      </header>

      <div className='flex gap-4 mt-8'>
        {/* input (left side)  */}
        <div className='w-3/5 bg-white px-4 py-2'>
          <h4 className='text-lg font-medium opacity-80'>Input</h4>
          <hr className='border-t border-black border-opacity-20 -mx-4 my-2' />

          <div className='mt-4 px-2'>
            {/* icon input */}
            <div className='mb-4'>
              <div className='text-sm opacity-30'>Session Icon</div>
              {this.state.theme.icon ? (
                <div className='inline-block relative'>
                  <RenderProfilePic thumbnail={this.state.theme.icon} />
                  <button
                    onClick={() => this.state.iconInputRef.current.click()}
                    className='absolute bottom-0 right-0 bg-blue-c1 text-white rounded-full px-1.5 py-1'
                  >
                    <Edit fontSize='small' />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => this.state.iconInputRef.current.click()}
                  className='bg-gray-c1 p-4 rounded-full'
                >
                  <Add fontSize='large' className='opacity-40' />
                </button>
              )}
              <input
                onChange={({ target }) => {
                  const iconLocalFile = target.files[0];
                  const theme = this.state.theme;
                  theme.icon = URL.createObjectURL(target.files[0]);
                  this.setState({ theme, iconLocalFile });
                }}
                type='file'
                ref={this.state.iconInputRef}
                accept='.jpg,.jpeg,.png'
                hidden
              />
            </div>

            <TextField
              onChange={({ target }) => {
                const theme = this.state.theme;
                theme.name = target.value;
                this.setState({ theme });
              }}
              value={this.state.theme.name || ''}
              label='Theme Title'
              variant='outlined'
              style={{ marginTop: '1rem' }}
              fullWidth
              autoFocus
            />

            <TextField
              onChange={({ target }) => {
                const theme = this.state.theme;
                theme.description = target.value;
                this.setState({ theme });
              }}
              value={this.state.theme.description || ''}
              label='Theme Description'
              variant='outlined'
              style={{ marginTop: '1rem' }}
              minRows={3}
              fullWidth
              multiline
            />

            <TextField
              onChange={({ target }) => {
                const theme = this.state.theme;
                theme.courseType = target.value;
                this.setState({ theme });
              }}
              value={this.state.theme.courseType || ''}
              label='Course Type'
              variant='outlined'
              style={{ marginTop: '1rem' }}
              fullWidth
              select
            >
              {Object.values(COURSE_TYPE).map((courseType) => (
                <MenuItem value={courseType} key={courseType}>
                  {COURSE_TYPE_NAMES_MAPPING[courseType]}
                </MenuItem>
              ))}
            </TextField>

            <div className='flex gap-12'>
              <div className='mt-4 flex items-center'>
                <span className='ml-2 mr-4 opacity-60 font-medium'>
                  AlmaX Theme
                </span>
                <CustomSwitch
                  checked={this.state.theme.isAlmaXTheme || false}
                  onChange={() => {
                    const theme = this.state.theme;
                    theme.isAlmaXTheme = !theme.isAlmaXTheme;
                    this.setState({ theme });
                  }}
                />
              </div>

              <div className='mt-4 flex items-center'>
                <span className='ml-2 mr-4 opacity-60 font-medium'>
                  Hidden Theme
                </span>
                <CustomSwitch
                  checked={this.state.theme.isHidden || false}
                  onChange={() => {
                    const theme = this.state.theme;
                    theme.isHidden = !theme.isHidden;
                    this.setState({ theme });
                  }}
                />
              </div>
              <div className='mt-4 flex items-center'>
                <span className='ml-2 mr-4 opacity-60 font-medium whitespace-nowrap'>
                  Track Selection
                </span>
                <TextField
                  onChange={({ target }) => {
                    const theme = this.state.theme;
                    theme.trackId = target.value;
                    this.setState({ theme });
                  }}
                  value={this.state.theme.trackId || ''}
                  label='Select Track'
                  variant='outlined'
                  size='small'
                  className='w-48'
                  select
                >
                  {this.state.tracks.map((track) => (
                    <MenuItem value={track.id} key={track.id}>
                      {track.attributes?.Name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>

            <hr className='border-t border-black border-opacity-20 my-6' />

            {/* topics list */}
            {this.state.theme?.topics?.map((topic, i) => (
              <React.Fragment key={i}>
                <TextField
                  onChange={({ target }) => {
                    const theme = this.state.theme;
                    theme.topics[i].name = target.value;
                    this.setState({ theme });
                  }}
                  value={topic.name || ''}
                  label='Topic'
                  variant='outlined'
                  autoFocus
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() =>
                          this.setState({
                            deleteConfirmPopUp: {
                              type: 'Topic',
                              name: topic.name,
                              delete: () => {
                                const theme = this.state.theme;
                                theme.topics.splice(i, 1);
                                this.setState({
                                  theme,
                                  deleteConfirmPopUp: null,
                                });
                              },
                            },
                          })
                        }
                      >
                        <Delete />
                      </IconButton>
                    ),
                  }}
                />

                {/* subtopics list */}
                {topic?.subTopics?.map((subTopic, j) => (
                  <div
                    key={j}
                    className={
                      this.state.expandedSubTopic[`${i}${j}`]
                        ? 'bg-blue-c1 bg-opacity-10 rounded-md p-2 mt-2 -mx-2'
                        : 'mt-3'
                    }
                  >
                    <TextField
                      onChange={({ target }) => {
                        const theme = this.state.theme;
                        theme.topics[i].subTopics[j].name = target.value;
                        this.setState({ theme });
                      }}
                      value={subTopic.name || ''}
                      label='SubTopic'
                      variant='outlined'
                      size='small'
                      autoFocus
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <>
                            {/* subtopic details form toggle */}
                            <IconButton
                              onClick={() => {
                                const expandedSubTopic = {
                                  [`${i}${j}`]:
                                    !this.state.expandedSubTopic[`${i}${j}`],
                                };
                                this.setState({ expandedSubTopic });
                              }}
                            >
                              {this.state.expandedSubTopic[`${i}${j}`] ? (
                                <ArrowDropUp />
                              ) : (
                                <ArrowDropDown />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                this.setState({
                                  deleteConfirmPopUp: {
                                    type: 'SubTopic',
                                    name: subTopic.name,
                                    delete: () => {
                                      const theme = this.state.theme;
                                      theme.topics[i].subTopics.splice(j, 1);
                                      this.setState({
                                        theme,
                                        deleteConfirmPopUp: null,
                                      });
                                    },
                                  },
                                })
                              }
                            >
                              <Delete />
                            </IconButton>
                          </>
                        ),
                      }}
                    />

                    {/* subtopic details form */}
                    {this.state.expandedSubTopic[`${i}${j}`] && (
                      <>
                        <div className='grid grid-cols-2 gap-3 mt-3'>
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].description =
                                target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.description || ''}
                            label='Description'
                            variant='outlined'
                            size='small'
                            className='col-span-2'
                            minRows={3}
                            multiline
                            autoFocus
                          />
                          <RichTextEditor
                            onChange={(value) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].instructionForCoach =
                                value;
                              this.setState({ theme });
                            }}
                            value={subTopic.instructionForCoach}
                            placeholder='Instructions For Coach'
                            className='col-span-2 mb-12'
                          />
                          <RichTextEditor
                            onChange={(value) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[
                                j
                              ].instructionForStudent = value;
                              this.setState({ theme });
                            }}
                            value={subTopic.instructionForStudent}
                            placeholder='Instructions For Student'
                            className='col-span-2 mb-12'
                          />
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].duration =
                                target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.duration || ''}
                            label='Duration (Mins)'
                            type='number'
                            variant='outlined'
                            size='small'
                          />
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].coins = target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.coins || ''}
                            label='Coins'
                            type='number'
                            variant='outlined'
                            size='small'
                          />
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].fixedEarning =
                                target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.fixedEarning || ''}
                            label='Fixed Earning'
                            type='number'
                            variant='outlined'
                            size='small'
                          />
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].variableEarning =
                                target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.variableEarning || ''}
                            label='Variable Earning'
                            type='number'
                            variant='outlined'
                            size='small'
                          />
                          <TextField
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[j].minQuestions =
                                target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.minQuestions || ''}
                            label='Min Questions'
                            type='number'
                            variant='outlined'
                            size='small'
                          />
                          <TextField
                            select
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[
                                j
                              ].ratingTemplateIdForStudent = target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.ratingTemplateIdForStudent || ''}
                            label='Rating Template For Student'
                            variant='outlined'
                            size='small'
                          >
                            {this.props.ratingTemplates?.map((rt) => (
                              <MenuItem value={rt.id} key={rt.id}>
                                {rt.title}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[
                                j
                              ].ratingTemplateIdForCoach = target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.ratingTemplateIdForCoach || ''}
                            label='Rating Template For Coach'
                            variant='outlined'
                            size='small'
                          >
                            {this.props.ratingTemplates?.map((rt) => (
                              <MenuItem value={rt.id} key={rt.id}>
                                {rt.title}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            onChange={({ target }) => {
                              const theme = this.state.theme;
                              theme.topics[i].subTopics[
                                j
                              ].ratingTemplateIdForAdmin = target.value;
                              this.setState({ theme });
                            }}
                            value={subTopic.ratingTemplateIdForAdmin || ''}
                            label='Rating Template For Admin'
                            variant='outlined'
                            size='small'
                          >
                            {this.props.ratingTemplates?.map((rt) => (
                              <MenuItem value={rt.id} key={rt.id}>
                                {rt.title}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>

                        <hr className='border-r border-black border-opacity-20 my-4' />

                        {subTopic.questionLevels.map((ql, k) => (
                          <div className='flex items-center gap-2 my-2' key={k}>
                            <TextField
                              select
                              onChange={({ target }) => {
                                const theme = this.state.theme;
                                theme.topics[i].subTopics[j].questionLevels[
                                  k
                                ].moduleId = target.value;
                                this.setState({ theme });
                              }}
                              value={ql.moduleId || ''}
                              label='Module (L1)'
                              variant='outlined'
                              size='small'
                              fullWidth
                            >
                              {this.props.modules?.map((mdl) => (
                                <MenuItem value={mdl.id} key={mdl.id}>
                                  {mdl.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              disabled={!ql.moduleId}
                              onChange={({ target }) => {
                                const theme = this.state.theme;
                                theme.topics[i].subTopics[j].questionLevels[
                                  k
                                ].competencyId = target.value;
                                this.setState({ theme });
                              }}
                              value={ql.competencyId || ''}
                              label='Competency (L2)'
                              variant='outlined'
                              size='small'
                              fullWidth
                            >
                              {this.props.competencies
                                ?.filter((cmp) => cmp.moduleId === ql.moduleId)
                                .map((cmp) => (
                                  <MenuItem value={cmp.id} key={cmp.id}>
                                    {cmp.name}
                                  </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                              select
                              disabled={!ql.competencyId}
                              onChange={({ target }) => {
                                const theme = this.state.theme;
                                theme.topics[i].subTopics[j].questionLevels[
                                  k
                                ].conceptId = target.value;
                                this.setState({ theme });
                              }}
                              value={ql.conceptId || ''}
                              label='Concept (L3)'
                              variant='outlined'
                              size='small'
                              fullWidth
                            >
                              {this.props.concepts
                                ?.filter(
                                  (cpt) => cpt.competencyId === ql.competencyId
                                )
                                .map((cpt) => (
                                  <MenuItem value={cpt.id} key={cpt.id}>
                                    {cpt.name}
                                  </MenuItem>
                                ))}
                            </TextField>

                            <button
                              className='opacity-60 px-2'
                              onClick={() => {
                                const { theme } = this.state;
                                theme.topics[i].subTopics[
                                  j
                                ].questionLevels.splice(k, 1);
                                this.setState({ theme });
                              }}
                            >
                              <Delete fontSize='small' />
                            </button>
                          </div>
                        ))}
                        <div className='text-right mt-3 mx-2'>
                          <button
                            className='text-blue-c1 font-medium'
                            onClick={() => {
                              const { theme } = this.state;
                              theme.topics[i].subTopics[j].questionLevels.push(
                                {}
                              );
                              this.setState({ theme });
                            }}
                          >
                            <Add className='-mt-1' />
                            <span>Add</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* add new subtopic */}
                <button
                  onClick={() => {
                    const theme = this.state.theme;
                    if (theme.topics[i].subTopics)
                      theme.topics[i].subTopics.push({});
                    else theme.topics[i].subTopics = [{}];
                    this.setState({ theme });
                  }}
                  className='text-blue-c1 font-medium flex items-center gap-1 mt-4 ml-4'
                  type='button'
                >
                  <Add className='-mt-0.5' />
                  <span>Add SubTopic</span>
                </button>

                <hr className='border-t border-black border-opacity-20 my-4' />
              </React.Fragment>
            ))}

            {/* add new topic */}
            <button
              onClick={() => {
                const theme = this.state.theme;
                const topic = {
                  name: null,
                  subTopics: [],
                };
                if (theme.topics) theme.topics.push(topic);
                else theme.topics = [topic];
                this.setState({ theme });
              }}
              className='text-blue-c1 text-lg font-medium flex items-center gap-1'
              type='button'
            >
              <Add className='-mt-0.5' />
              <span>Add Topic</span>
            </button>

            {/* save button */}
            <button
              onClick={this.handleSubmit}
              disabled={this.state.isLoading.saving}
              className='bg-blue-c1 text-white text-medium text-lg rounded-md py-1 w-full my-4'
            >
              {this.state.isLoading.saving ? (
                <CircularProgress
                  color='inherit'
                  size='1.5rem'
                  className='-my-1.5'
                />
              ) : (
                <span>Save Changes</span>
              )}
            </button>
          </div>
        </div>

        {/* preview (right side) */}
        <div className='w-2/5 bg-white px-4 py-2'>
          <h4 className='text-lg font-medium opacity-80'>Live Preview</h4>
          <hr className='border-t border-black border-opacity-20 -mx-4 mt-2 mb-4' />

          {this.state.theme.icon && (
            <RenderProfilePic thumbnail={this.state.theme.icon} />
          )}
          <h6 className='text-2xl font-medium opacity-80 mt-3'>
            {this.state.theme.name}
          </h6>
          <p className='opacity-60 pt-2'>{this.state.theme.description}</p>

          <hr className='border-t border-black border-opacity-20 mt-4' />

          {this.state.theme?.topics?.map((topic, i) => (
            <div className='mt-8' key={i}>
              <div className='text-xl text-green-c1 font-medium opacity-80 mb-4'>
                {topic.name}
              </div>

              {topic?.subTopics?.map((subTopic, j) => (
                <div key={j}>
                  <div className='flex items-center justify-between -my-2'>
                    <span className='opacity-60 font-medium'>
                      {subTopic.name}
                    </span>

                    {/* subtopic details toggle */}
                    <IconButton
                      onClick={() => {
                        const expandedSubTopic = {
                          [`${i}${j}`]:
                            !this.state.expandedSubTopic[`${i}${j}`],
                        };
                        this.setState({ expandedSubTopic });
                      }}
                    >
                      {this.state.expandedSubTopic[`${i}${j}`] ? (
                        <ArrowDropUp />
                      ) : (
                        <ArrowDropDown />
                      )}
                    </IconButton>
                  </div>

                  {this.state.expandedSubTopic[`${i}${j}`] && (
                    <div className='grid grid-cols-2 gap-y-2 opacity-60 text-sm mb-4'>
                      <p className='col-span-2'>{subTopic.description}</p>
                      <span>Duration: {subTopic.duration || 'N/A'} Mins</span>
                      <span>Coins: {subTopic.coins || 'N/A'}</span>
                      <span>
                        Fixed Earning: ₹ {subTopic.fixedEarning || 'N/A'}
                      </span>
                      <span>
                        Variable Earning: ₹ {subTopic.variableEarning || 'N/A'}
                      </span>
                      <span>
                        Min Questions: {subTopic.minQuestions || 'N/A'}
                      </span>
                      <span>
                        Max Own Questions: {subTopic.maxOwnQuestions || 'N/A'}
                      </span>
                      <span>Min Date: {subTopic.minDate || 'N/A'}</span>
                      <span>Max Date: {subTopic.maxDate || 'N/A'}</span>
                      <span className='col-span-2'>
                        Module (L1):{' '}
                        {this.props.moduleNameByModuleId[subTopic.moduleId] ||
                          'N/A'}
                      </span>
                      <span className='col-span-2'>
                        Competency (L2):{' '}
                        {this.props.competencyNameByCompetencyId[
                          subTopic.competencyId
                        ] || 'N/A'}
                      </span>
                      <span className='col-span-2'>
                        Rating Template for Student:{' '}
                        {this.props.ratingTemplateNameByRatingTemplateId[
                          subTopic.ratingTemplateIdForStudent
                        ] || 'N/A'}
                      </span>
                      <span className='col-span-2'>
                        Rating Template for Coach:{' '}
                        {this.props.ratingTemplateNameByRatingTemplateId[
                          subTopic.ratingTemplateIdForCoach
                        ] || 'N/A'}
                      </span>
                      <span className='col-span-2'>
                        Rating Template for Admin:{' '}
                        {this.props.ratingTemplateNameByRatingTemplateId[
                          subTopic.ratingTemplateIdForAdmin
                        ] || 'N/A'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {this.state.deleteConfirmPopUp && (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-25 flex'>
          <div className='m-auto bg-white rounded-lg text-center p-4'>
            <div className='text-2xl font-semibold opacity-80 pt-4'>
              Delete {this.state.deleteConfirmPopUp.type} ?
            </div>
            <div className='text-lg font-medium opacity-60 p-4'>
              Are you sure you want to delete `
              {this.state.deleteConfirmPopUp.name}` ?
            </div>

            <hr className='border-t border-black border-opacity-10 my-4' />

            <div className='text-right'>
              <button
                className='opacity-40 font-medium rounded-md px-8 py-2'
                disabled={this.state.isLoading.deleting}
                onClick={() => this.setState({ deleteConfirmPopUp: null })}
              >
                Cancel
              </button>
              <button
                className='bg-red-c1 text-white font-medium rounded-md py-2 w-32'
                onClick={() => this.state.deleteConfirmPopUp.delete()}
                disabled={this.state.isLoading.deleting}
              >
                {this.state.isLoading.deleting ? (
                  <CircularProgress
                    color='inherit'
                    size='1.5rem'
                    className='-my-1.5'
                  />
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  ratingTemplates: state.ratingTemplateReducer.ratingTemplates,
  ratingTemplateNameByRatingTemplateId: _.fromPairs(
    state.ratingTemplateReducer.ratingTemplates?.map(({ id, title }) => [
      id,
      title,
    ])
  ),
  modules: state.questionLevelsReducer.modules,
  moduleNameByModuleId: _.fromPairs(
    state.questionLevelsReducer.modules.map(({ id, name }) => [id, name])
  ),
  competencies: state.questionLevelsReducer.competencies,
  competencyNameByCompetencyId: _.fromPairs(
    state.questionLevelsReducer.competencies.map(({ id, name }) => [id, name])
  ),
  moduleIdByCompetencyId: state.questionLevelsReducer.moduleIdByCompetencyId,
  concepts: state.questionLevelsReducer.concepts,
});

export default connect(mapStateToProps)(AddOrEditSession);
